import { api } from "../../api/client.ts";
import type { Issue, Severity, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { toast } from "../../lib/toast.ts";
import { DismissedIcon, MoreIcon, ResolvedIcon } from "../../ui/icons.tsx";
import { Menu } from "../../ui/Menu.tsx";
import { SeverityPicker } from "../../ui/SeverityPicker.tsx";
import { iconButtonClass } from "../../ui/styles.ts";

/**
 * What can be done to a reviewer's finding right here (anyone's, one's own included): another severity, or closing
 * it as resolved or dismissed. Saved as a review of one's own that keeps one's findings as they were.
 */
export function FindingActions({
  space,
  item,
  asOf,
  issue,
  onChange,
}: {
  space: SpaceOut;
  /** The item it's about. */
  item: string;
  /** What the item looked like when read (`head`), if known. */
  asOf?: string | undefined;
  issue: Pick<Issue, "id" | "severity" | "message">;
  onChange: () => void;
}) {
  const id = issue.id ?? "";
  const [act] = useAction(
    async (change: { adjust?: Record<string, Severity>; resolve?: string[]; dismiss?: string[] }) => {
      await api.post(spaceApi(space, "/reviews"), {
        item,
        keep_findings: true,
        ...(asOf && { as_of: asOf }),
        ...change,
      });
      toast(t("review.saved"));
      onChange();
    },
  );
  return (
    <Menu
      label={
        <>
          <MoreIcon />
          <span class="sr-only">{t("issues.actions", { message: issue.message })}</span>
        </>
      }
      class={`-my-1 ${iconButtonClass("xs")}`}
      items={[
        {
          key: "severity",
          group: t("issues.severityGroup"),
          content: (close: () => void) => (
            <SeverityPicker
              value={issue.severity}
              label={t("issues.severityGroup")}
              onValue={(s) => {
                close();
                if (s !== issue.severity) {
                  act({ adjust: { [id]: s } });
                }
              }}
            />
          ),
        },
        {
          group: t("issues.closeGroup"),
          label: t("issues.markResolved"),
          icon: <ResolvedIcon size={16} />,
          onSelect: () => act({ resolve: [id] }),
        },
        {
          group: t("issues.closeGroup"),
          label: t("issues.markDismissed"),
          icon: <DismissedIcon size={16} />,
          onSelect: () => act({ dismiss: [id] }),
        },
      ]}
    />
  );
}
